import { defineStore } from 'pinia'
import { useToast } from '@/components/ui/toast/use-toast'
import { useRuntimeConfig } from '#app'
import { logError } from '@/composables/useLogger'

export interface BubbleStats {
  did: boolean
  rating: number | null
  review: string | null
  createdAt: string
  updatedAt: string
}

interface APIStatsResponse {
  did?: {
    did: boolean
    created_at: string
    updated_at: string
  }
  rating?: {
    rating: number
    created_at: string
    updated_at: string
  }
  review?: {
    review: string
    created_at: string
    updated_at: string
  }
}

export interface BubbleStatsResponse {
  stats: Record<string, APIStatsResponse>
}

export const useStatsStore = defineStore('stats', {
  state: () => ({
    stats: {} as Record<string, BubbleStats>,
    isLoading: false,
  }),

  actions: {
    transformSingleResponse(data: APIStatsResponse): BubbleStats {
      return {
        did: data.did?.did ?? false,
        rating: data.rating?.rating ?? null,
        review: data.review?.review ?? null,
        createdAt:
          data.did?.created_at ||
          data.rating?.created_at ||
          data.review?.created_at ||
          new Date().toISOString(),
        updatedAt:
          data.did?.updated_at ||
          data.rating?.updated_at ||
          data.review?.updated_at ||
          new Date().toISOString(),
      }
    },

    async fetchBulkBubbleStats() {
      const { toast } = useToast()
      const config = useRuntimeConfig()
      this.isLoading = true

      try {
        const response = await fetch(`${config.public.apiBase}/stats/bulk`, {
          method: 'GET',
          credentials: 'include',
        })

        if (!response.ok) throw new Error('Failed to fetch bulk stats')

        const data: BubbleStatsResponse = await response.json()
        // Transform each stat before merging
        const transformedStats = Object.entries(data.stats).reduce(
          (acc, [key, value]) => {
            acc[key] = this.transformSingleResponse(value)
            return acc
          },
          {} as Record<string, BubbleStats>
        )

        this.stats = { ...this.stats, ...transformedStats }
        return transformedStats
      } catch (error) {
        logError('Failed to fetch bulk stats:', error)
        toast({
          title: 'Error',
          description: 'Failed to load bulk stats',
          variant: 'destructive',
        })
        return null
      } finally {
        this.isLoading = false
      }
    },

    async fetchBubbleStats(bubbleUuid: string) {
      const { toast } = useToast()
      const config = useRuntimeConfig()
      this.isLoading = true

      try {
        const response = await fetch(`${config.public.apiBase}/stats/${bubbleUuid}`, {
          credentials: 'include',
        })

        if (!response.ok) throw new Error('Failed to fetch stats')

        const data: APIStatsResponse = await response.json()
        const transformed = this.transformSingleResponse(data)
        this.stats[bubbleUuid] = transformed
        return transformed
      } catch (error) {
        logError('Failed to fetch stats:', error)
        toast({
          title: 'Error',
          description: 'Failed to load stats',
          variant: 'destructive',
        })
        return null
      } finally {
        this.isLoading = false
      }
    },

    async toggleDid(bubbleUuid: string) {
      const { toast } = useToast()
      const config = useRuntimeConfig()

      try {
        const response = await fetch(`${config.public.apiBase}/stats/${bubbleUuid}/did`, {
          method: 'POST',
          credentials: 'include',
        })

        if (!response.ok) throw new Error('Failed to toggle did status')

        const data = await response.json()
        if (this.stats[bubbleUuid]) {
          this.stats[bubbleUuid] = { ...this.stats[bubbleUuid], did: data.did }
        }
        return data
      } catch (error) {
        logError('Failed to toggle did status:', error)
        toast({
          title: 'Error',
          description: 'Failed to update status',
          variant: 'destructive',
        })
        return null
      }
    },

    async updateRating(bubbleUuid: string, rating: number) {
      const { toast } = useToast()
      const config = useRuntimeConfig()

      try {
        const response = await fetch(`${config.public.apiBase}/stats/${bubbleUuid}/rating`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ rating }),
        })

        if (!response.ok) throw new Error('Failed to update rating')

        const data = await response.json()
        if (this.stats[bubbleUuid]) {
          this.stats[bubbleUuid] = { ...this.stats[bubbleUuid], rating: data.rating }
        }
        return data
      } catch (error) {
        logError('Failed to update rating:', error)
        toast({
          title: 'Error',
          description: 'Failed to update rating',
          variant: 'destructive',
        })
        return null
      }
    },

    async updateReview(bubbleUuid: string, review: string) {
      const { toast } = useToast()
      const config = useRuntimeConfig()

      try {
        const response = await fetch(`${config.public.apiBase}/stats/${bubbleUuid}/review`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ review }),
        })

        if (!response.ok) throw new Error('Failed to update review')

        const data = await response.json()
        if (this.stats[bubbleUuid]) {
          this.stats[bubbleUuid] = { ...this.stats[bubbleUuid], review: data.review }
        }
        return data
      } catch (error) {
        logError('Failed to update review:', error)
        toast({
          title: 'Error',
          description: 'Failed to update review',
          variant: 'destructive',
        })
        return null
      }
    },
  },
})
